import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Avatar,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
} from "@mui/material";
import {
  Edit,
  Delete,
  Visibility,
  FilterList,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import WorkIcon from "@mui/icons-material/Work";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useLocation, useNavigate } from "react-router-dom";
import SelectInput from "../../Components/InputComponents/SelectInput";
import CommonButton from "../../Components/InputComponents/CommonButton";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const CandidateData = [
  {
    name: "Sarah Johnson",
    status: "Interview Attended",
  },
  {
    name: "Michael Chen",
    status: "Offer Letter Sent",
  },
  {
    name: "Emily Davis",
    status: "Interview Rescheduled",
  },
  {
    name: "James Wilson",
    status: "Interview On Hold",
  },
  {
    name: "Maria Garcia",
    status: "Offer Letter Accepted",
  },
];

const CandidateList = ({ jobId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [interviewStatus, setInterviewStatus] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSave = () => {
    // Save interviewStatus somewhere
    handleClose();
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filters, setFilters] = useState({
    name: "",
    status: "",
  });

  const filteredCandidates = CandidateData?.filter((parent) => {
    const nameMatch = parent.name
      ?.toLowerCase()
      ?.includes(appliedFilters.name?.toLowerCase() || "");

    const statusMatch = parent.status
      ?.toLowerCase()
      ?.includes(appliedFilters.status?.toLowerCase() || "");
    return nameMatch && statusMatch;
  });

  const handleApplyFilters = () => setAppliedFilters(filters);

  const handleResetFilters = () => {
    setFilters({ name: "", phone: "", address: "", status: "" });
    setAppliedFilters({});
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const navigatetoView = (id) =>
    navigate(`/Candidates/View/${id}`, {
      state: { from: `/Jobs/View/${jobId}` },
    });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Paper
      sx={{
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
        boxShadow: "none",
        border: "1px solid lightgrey",
        p: 3,
        borderRadius: "20px",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Job Applicants
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          placeholder="Name..."
          variant="outlined"
          size="small"
          value={filters.name}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, name: e.target.value }));
          }}
          sx={{ width: { xs: "100%" } }}
        />

        <TextField
          placeholder="Status"
          variant="outlined"
          size="small"
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, status: e.target.value }));
          }}
          value={filters.status}
          sx={{ width: { xs: "100%" } }}
        />

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Search />}
            onClick={handleApplyFilters}
            sx={{
              textTransform: "capitalize",
              color: "white",
              background: "#006CB8",
            }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<RestartAlt />}
            onClick={handleResetFilters}
            sx={{ textTransform: "capitalize" }}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{
              fontFamily: "Geist, sans-serif",
              textTransform: "capitalize",
              fontSize: { xs: "12px", sm: "14px" },
              padding: { xs: "5px 10px", sm: "6px 12px" },
            }}
          >
            Filter
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid lightgray",
          borderRadius: "10px",
          maxWidth: "100vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width:"50px" }}>#</StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCandidates && filteredCandidates?.length > 0 ? (
              filteredCandidates
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((c, index) => (
                  <StyledTableRow key={c.id}>
                    <StyledTableCell>
                      {index + 1 + page * rowsPerPage}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box display="flex" alignItems="center">
                       
                        <Typography
                          sx={{
                            fontSize: { xs: "12px", sm: "14px" },
                            fontFamily: "Geist, sans-serif",
                          }}
                        >
                          {c.name}
                        </Typography>
                      </Box>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Box display="flex" alignItems="center">
                        <Typography
                          sx={{
                            fontSize: { xs: "12px", sm: "14px" },
                            fontFamily: "Geist, sans-serif",
                          }}
                        >
                          {c.status}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          navigatetoView(index + 1);
                        }}
                      >
                        <Visibility />
                      </IconButton>

                      <IconButton color="warning" onClick={handleOpen}>
                        <Edit />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={9} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            color: "#0977BC",
            mt: 3,
            mb: 2,
            fontFamily: "Geist, sans-serif",
          }}
        >
          Edit Interview Status
        </DialogTitle>

        <DialogContent>
          <Box>
            <SelectInput
              
              placeholder="Select Interview Status"
              name="interview_status"
              value={interviewStatus}
              onChange={(e) => {
                setInterviewStatus(e.target.value);
              }}
              options={[
                { value: "interview_attended", label: "Interview Attended" },
                {
                  value: "interview_rescheduled",
                  label: "Interview Rescheduled",
                },
                { value: "interview_on_hold", label: "Interview On Hold" },
                { value: "interview_rejected", label: "Interview Rejected" },
                { value: "offer_letter_sent", label: "Offer Letter Sent" },
                {
                  value: "offer_letter_accepted",
                  label: "Offer Letter Accepted",
                },
                {
                  value: "offer_letter_rejected",
                  label: "Offer Letter Rejected",
                },
              ]}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ mb: 3, mr: 3 }}>
          <Box display="flex" gap={2}>
            <CommonButton
              variant="outlined"
              customColor="#B0BEC5"
              onClick={handleClose}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              customColor="#007BFF"
              onClick={handleSave}
            >
              Save
            </CommonButton>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredCandidates.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default CandidateList;
